import { useMutation, useQuery } from '@apollo/client';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { useCallback, useState } from 'react';
import { GET_JOB_APPLICATION_SCORING, SCORE_JOB_APPLICATION } from '@gql/jobs';

export const useScoreJobApplication = (
    jobAppId: string,
    scoringStatus?: string | null
) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showSnackbar } = useSnackbar();

    const [scoreJobApplicationMutation] = useMutation(SCORE_JOB_APPLICATION);

    // Poll every 5s when the backend is waiting for Trimoji to deliver the CV or personi
    const { data: pollData } = useQuery(GET_JOB_APPLICATION_SCORING, {
        variables: { id: jobAppId },
        // one minute timeout to avoid polling indefinitely in case of unexpected issues
        pollInterval: 5000,
        skip: scoringStatus !== 'pending_cv',
    });

    // Use live polled scoringStatus if available, fall back to the prop
    const effectiveScoringStatus = pollData?.getJobApplication?.scoringStatus ?? scoringStatus;

    const handleScore = useCallback(async () => {
        setIsSubmitting(true);
        try {
            await scoreJobApplicationMutation({
                variables: { jobApplicationId: jobAppId },
                update: (cache, { data: mutationData }) => {
                    const updated = mutationData?.scoreJobApplication;
                    if (!updated) return;
                    const cacheId = cache.identify({ __typename: 'JobApplication', _id: updated._id });
                    if (!cacheId) return;
                    cache.modify({
                        id: cacheId,
                        fields: {
                            resumeScore: () => updated.resumeScore,
                            resumeScoreError: () => updated.resumeScoreError,
                            resumeExplainer: () => updated.resumeExplainer,
                            scoringStatus: () => updated.scoringStatus,
                        },
                    });
                },
            });
        } catch (error) {
            showSnackbar(error?.message ?? 'Une erreur est survenue lors du scoring.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    }, [scoreJobApplicationMutation, jobAppId, showSnackbar]);

    const isScoring =
        isSubmitting ||
        effectiveScoringStatus === 'scoring' ||
        effectiveScoringStatus === 'pending_cv';

    return { handleScore, isScoring, scoringStatus: effectiveScoringStatus };
};
